// docs/116 CI5: the dice table seen from the room. Three tiny dice that hop on every throw, the pot as a
// few coins and each hand's score floating over its stool. It draws only what is on the table top; the
// room's own painter draws the table and the stools, and the lead places this over the table.
import { useEffect, useRef, type CSSProperties } from 'react';
import { useReducedMotion } from '../../../components';
import { hashInts, handName } from './dice';
import { dieTiming } from './diceMotion';
import type { DiceView } from './types';
import './dice.css';

export interface RoomDiceProps {
  view: DiceView;
  /** The village view's whole-number scale (a cell is 24·scale px). */
  scale: number;
  /** The table's size in cells. */
  cols: number;
  rows: number;
  /** Where each of the four stools' scores float, in px from the table's top-left (default: along its top edge). */
  stools?: Array<{ x: number; y: number }>;
}

const INK = '#2a1a12', IVORY = '#efe5cc', LO = '#c4b28a';
const PIP5: Record<number, Array<[number, number]>> = {
  1: [[1, 1]], 2: [[0, 0], [2, 2]], 3: [[0, 0], [1, 1], [2, 2]], 4: [[0, 0], [2, 0], [0, 2], [2, 2]], 5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]], 6: [[0, 0], [2, 0], [0, 1], [2, 1], [0, 2], [2, 2]],
};

function tinyDie(c: CanvasRenderingContext2D, x: number, y: number, f: number) {
  c.fillStyle = INK; c.fillRect(x, y, 5, 5);
  c.fillStyle = IVORY; c.fillRect(x + 1, y + 1, 3, 3);
  c.fillStyle = LO; c.fillRect(x + 1, y + 3, 3, 1);
  for (const [i, j] of PIP5[f] ?? []) { c.fillStyle = f === 1 ? '#a3241c' : INK; c.fillRect(x + 1 + i, y + 1 + j, 1, 1); }
}

export function RoomDice({ view, scale, cols, rows, stools }: RoomDiceProps) {
  const reduced = useReducedMotion();
  const cv = useRef<HTMLCanvasElement>(null);
  const live = useRef({ view, reduced });
  live.current = { view, reduced };
  const hop = useRef<{ throwN: number; t0: number; mask: number }>({ throwN: view.throwN, t0: 0, mask: 0 });
  if (hop.current.throwN !== view.throwN) hop.current = { throwN: view.throwN, t0: performance.now(), mask: view.thrown };
  const W = cols * 24, H = rows * 24;

  useEffect(() => {
    const c = cv.current?.getContext('2d');
    if (!c) return;
    c.imageSmoothingEnabled = false;
    let raf = 0;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const { view: v, reduced: still } = live.current;
      const t = performance.now();
      c.clearRect(0, 0, W, H);
      // the pot: a few coins in the middle
      const coins = Math.min(8, Math.ceil(v.pot / 2)) + (v.kitty ? 2 : 0);
      for (let k = 0; k < coins; k++) {
        const h = hashInts(k, 3);
        const x = Math.round(W / 2 - 4 + (h % 9) - 2), y = Math.round(H / 2 - 5 + ((h >>> 8) % 4) - Math.floor(k / 3));
        c.fillStyle = '#5e3c0c'; c.fillRect(x, y + 1, 3, 1);
        c.fillStyle = k % 2 ? '#f2c14e' : '#e0a83a'; c.fillRect(x, y, 3, 1);
      }
      // three tiny dice that hop on each throw
      const a = hop.current;
      for (let i = 0; i < 3; i++) {
        const f = v.dice[i];
        if (!f) continue;
        const h = hashInts(a.throwN, i, 7);
        const x = Math.round(W / 2 - 10 + i * 7 + (h % 3) - 1), y0 = Math.round(H / 2 + (h >>> 4) % 3);
        let z = 0, face = f;
        if (!still && a.mask & (1 << i)) {
          const tm = dieTiming(a.throwN, i), ms = t - a.t0;
          if (ms < tm.end) {
            const u = Math.max(0, Math.min(1, ms / tm.end));
            z = Math.round(6 * Math.abs(Math.sin(u * Math.PI * 2.2)) * (1 - u));
            if (ms < tm.hits[2]) face = 1 + (hashInts(a.throwN, i, Math.floor(ms / 90)) % 6);
          }
        }
        c.fillStyle = 'rgba(0,0,0,0.35)'; c.fillRect(x, y0 + 5, 5, 1);
        tinyDie(c, x, y0 - z, face);
      }
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [W, H]);

  const spots = stools ?? [0, 1, 2, 3].map((i) => ({ x: ((i + 0.5) / 4) * W * scale, y: -2 * scale }));
  return (
    <div className="fk-roomdice" style={{ width: W * scale, height: H * scale, '--s': scale } as CSSProperties} aria-hidden="true">
      <canvas ref={cv} className="fk-roomdice__canvas" width={W} height={H} />
      {view.seats.map((s, i) => {
        if (s.kind === 'empty' || view.state === 'open') return null;
        const sp = spots[i];
        if (!sp) return null;
        const playing = view.state === 'playing' && view.turn === i && s.state === 'playing';
        const text = s.won ? `+${s.won}` : s.state === 'stood' ? (s.score >= 19 ? handName(s.hand) : String(s.score)) : s.state === 'bust' ? 'bust' : playing ? '…' : '';
        if (!text) return null;
        const cls = `fk-roomdice__score ${s.won || s.score >= 19 ? 'fk-roomdice__score--gold' : ''} ${s.state === 'bust' ? 'fk-roomdice__score--red' : ''} ${playing ? 'fk-roomdice__score--turn' : ''}`;
        return <span key={`${i}:${text}`} className={cls} style={{ left: sp.x, top: sp.y }}>{text}</span>;
      })}
    </div>
  );
}
